import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { NAVIGATION_CONSTANTS } from '../constants/navigation';
import { Component as AnimatedBackground } from './ui/raycast-red-blue-animated-background';
import { Button } from './ui/button';
import { Shield, Sparkles, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import WaitlistForm from './WaitlistForm';
import AntixBentoGrid from './AntixBentoGrid';
import AntixVelocityScroll from './AntixVelocityScroll';
import AboutSection from './AboutSection';
import BetaRecruitment from './BetaRecruitment';
import Navigation from './Navigation';
import MobileNavigation from './MobileNavigation';
import PartnersSection from './PartnersSection';
import MissionSection from './MissionSection';
import AnimatedFeatureCarousel from './AnimatedFeatureCarousel';
import { ZoomParallax } from './ui/zoom-parallax';
import { BackgroundGradientAnimation } from './ui/background-gradient-animation';

const AntixLanding = () => {
  const location = useLocation();

  // Handle scrolling to section when navigating from legal pages
  useEffect(() => {
    if (location.state?.scrollTo) {
      const timer = setTimeout(() => {
        const element = document.getElementById(location.state.scrollTo);
        if (element) {
          const targetPosition = element.offsetTop - NAVIGATION_CONSTANTS.NAV_HEIGHT;
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      }, NAVIGATION_CONSTANTS.SCROLL_TIMEOUT);
      
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  return (
    <div className="min-h-screen text-white">
      {/* Desktop Navigation */}
      <Navigation />
      {/* Mobile Navigation */}
      <MobileNavigation />
      {/* Hero Section */}
      <section data-theme="dark" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#000000] pt-40">
        {/* Animated Background */}
        <div className="absolute inset-0 z-0">
          <AnimatedBackground />
        </div>
        
        {/* Hero Content */}
        <div className="relative z-20 text-center px-6 max-w-4xl mx-auto">
          <h1 className="display-hero text-white mb-6 text-[52px] md:text-[120px] font-bold leading-tight">
            All your gigs.<br />One place.
          </h1>
          <p className="body-large text-soft-white/90 mb-8 max-w-2xl mx-auto text-[16px] md:text-[18px] lg:text-[20px] leading-relaxed">
            Antix is where your live events come together: tickets, plans with friends, and the memories you’ll never forget. All in one app
          </p>
          <WaitlistForm variant="hero" className="max-w-lg mx-auto" formPosition="hero" />
          <BetaRecruitment />
        </div>
      </section>

      {/* Transition Section */}
      <section data-theme="dark" className="bg-[#000000]">
        {/* About Section */}
        <AboutSection />

        {/* Bento Grid Section */}
        <AntixBentoGrid />

        {/* Animated Feature Carousel */}
        <AnimatedFeatureCarousel />

        {/* Partners Section */}
        <PartnersSection />

        {/* Zoom Parallax Section */}
        <ZoomParallax 
          images={[
            {
              src: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&h=600&fit=crop&crop=faces",
              alt: "Concert crowd"
            },
            {
              src: "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=800&h=600&fit=crop&crop=faces",
              alt: "Live music performance"
            },
            {
              src: "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=800&h=600&fit=crop&crop=faces",
              alt: "Festival stage"
            },
            {
              src: "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=800&h=600&fit=crop&crop=faces",
              alt: "Music festival"
            },
            {
              src: "https://customer-assets.emergentagent.com/job_web-ui-enhance/artifacts/7w5aqnwq_beaver-fernandez-C2pxiKp6-uA-unsplash.jpg",
              alt: "Concert performance"
            },
            {
              src: "https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=800&h=600&fit=crop&crop=faces",
              alt: "DJ performance"
            },
            {
              src: "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=800&h=600&fit=crop&crop=faces",
              alt: "Live band"
            }
          ]}
        />

        {/* Mission Section */}
        <MissionSection />

        {/* Final CTA Section */}
        <div data-theme="light" id="join" className="py-12 md:py-14 md:px-6 bg-white">
          <div className="container mx-auto">
            {/* Waitlist Box with Gradient Background Animation */}
            <div className="relative w-full max-w-6xl mx-auto p-6 py-8 md:p-12 md:py-16 rounded-[32px] overflow-hidden">
              {/* Background Gradient Animation */}
              <BackgroundGradientAnimation
                gradientBackgroundStart="rgb(108, 0, 162)"
                gradientBackgroundEnd="rgb(0, 17, 82)"
                firstColor="18, 113, 255"
                secondColor="221, 74, 255"
                thirdColor="100, 220, 255"
                fourthColor="200, 50, 50"
                fifthColor="180, 180, 50"
                pointerColor="140, 100, 255"
                containerClassName="absolute inset-0 w-full h-full"
              />
              
              {/* Content */}
              <div className="relative z-10 text-center">
                <h2 className="text-[36px] md:text-[48px] font-semibold text-white mb-6 leading-tight font-['Space_Grotesk']">
                  Subscribe and join us on this journey.
                </h2>
                <p className="text-[20px] md:text-[24px] text-gray-300 mb-8 leading-relaxed">
                  Get early access and help shape the future of Antix.
                </p>
                <WaitlistForm variant="bottom" className="max-w-lg mx-auto" formPosition="footer" />
              </div>
            </div>
          </div>
        </div>

        {/* Velocity Scroll Section */}
        <div data-theme="dark">
          <AntixVelocityScroll />
        </div>

        {/* Footer */}
        <footer data-theme="light" className="py-12 md:px-6 bg-white">
          <div className="container mx-auto px-6 md:px-0">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center space-y-8 md:space-y-0">
              {/* Copyright on left for desktop, centered on mobile */}
              <p className="body-small text-black/60 text-center md:text-left order-2 md:order-1">
                © 2025 Antix. All rights reserved.
              </p>
              
              {/* Social Icons - centered on mobile, middle on desktop */}
              <div className="flex justify-center order-1 md:order-2 mb-8 md:mb-0">
                <div className="flex space-x-6">
                  {/* Instagram */}
                  <a
                    href="https://www.instagram.com/antix.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group"
                    aria-label="Instagram"
                    onClick={() => {
                      if (window.gtag) {
                        window.gtag('event', 'social_click', {
                          event_category: 'Social',
                          event_label: 'instagram_footer',
                        });
                      }
                    }}
                  >
                    <svg 
                      className="w-6 h-6 fill-black group-hover:fill-transparent transition-all duration-300" 
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <defs>
                        <linearGradient id="instagram-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#405de6"/>
                          <stop offset="25%" stopColor="#5851db"/>
                          <stop offset="50%" stopColor="#833ab4"/>
                          <stop offset="75%" stopColor="#c13584"/>
                          <stop offset="100%" stopColor="#fd1d1d"/>
                        </linearGradient>
                      </defs>
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" 
                        className="group-hover:fill-[url(#instagram-gradient)]"
                      />
                    </svg>
                  </a>

                  {/* TikTok */}
                  <a
                    href="#"
                    className="group"
                    aria-label="TikTok"
                    onClick={() => {
                      if (window.gtag) {
                        window.gtag('event', 'social_click', {
                          event_category: 'Social',
                          event_label: 'tiktok_footer',
                        });
                      }
                    }}
                  >
                    <svg 
                      className="w-6 h-6 fill-black group-hover:fill-white transition-all duration-300" 
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <defs>
                        <linearGradient id="tiktok-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#ff0050"/>
                          <stop offset="50%" stopColor="#00f2ea"/>
                          <stop offset="100%" stopColor="#ff0050"/>
                        </linearGradient>
                      </defs>
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" 
                        className="group-hover:fill-[url(#tiktok-gradient)]"
                      />
                    </svg>
                  </a>

                  {/* LinkedIn */}
                  <a
                    href="#"
                    className="group"
                    aria-label="LinkedIn"
                    onClick={() => {
                      if (window.gtag) {
                        window.gtag('event', 'social_click', {
                          event_category: 'Social',
                          event_label: 'linkedin_footer',
                        });
                      }
                    }}
                  >
                    <svg 
                      className="w-6 h-6 fill-black group-hover:fill-[#0077b5] transition-all duration-300" 
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>
                </div>
              </div>

              {/* Links on right for desktop, below social icons on mobile */}
              <div className="flex justify-center md:justify-end space-x-8 order-3">
                <Link
                  to="/privacy-policy"
                  className="body-small text-black/80 hover:text-black/80 hover:underline transition-all duration-200"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/antix-app-privacy"
                  className="body-small text-black/80 hover:text-black/80 hover:underline transition-all duration-200"
                >
                  App Privacy Policy
                </Link>
                <Link
                  to="/terms-of-service"
                  className="body-small text-black/80 hover:text-black/80 hover:underline transition-all duration-200"
                >
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </section>
    </div>
  );
};

export default AntixLanding;
